import { Button, ButtonBase, Chip, Grid, Paper, Typography, colors } from '@material-ui/core';
import { unstable_Box as Box } from '@material-ui/core/Box';
import { withStyles } from '@material-ui/core/styles';
import { FaceOutlined } from '@material-ui/icons';
import classNames from 'classnames';
import moment from 'moment';
import React from 'react';
import { connect } from 'react-redux';

import { Event } from '@aps-management/primapp-common';

import { Screen } from '_components/core';
import { CalendarDay } from '_components/elements';
import apolloClient from '_utils/apolloClient';

/* */
import i18n from '_utils/i18n';

/* */
const styles = (theme) => ({
  item: {
    height: '100%',
    overflow: 'hidden',
    position: 'relative',
    padding: theme.spacing.unit * 2,
  },
  chip: {
    display: 'flex',
    borderColor: 'transparent',
    justifyContent: 'flex-start',
  },
  chipIcon: {
    marginLeft: 0,
    marginRight: theme.spacing.unit * -0.5,
  },
  canceled: {
    top: 16,
    right: -90,
    width: 250,
    overflow: 'hidden',
    fontWeight: 'bold',
    lineHeight: '30px',
    textAlign: 'center',
    position: 'absolute',
    transform: 'rotate(40deg)',
    boxShadow: theme.shadows[3],
    color: theme.palette.common.white,
    backgroundColor: theme.palette.error.dark,
  },
  itemContainer: { display: 'block' },
  registeredAt: { color: colors.green[500] },
  itemCanceled: { backgroundColor: theme.palette.grey[200] },
});

const LIMIT = 3;

/* */
class EventHistory extends React.Component {
  /* */
  state = {
    // Data
    last: [],
    comingSoon: [],
    // UX
    showMore: null,
    // Screen
    error: null,
    loading: true,
  };

  /* */
  componentDidMount() {
    this.load();
  }

  /* */
  load = () => {
    this.setState({
      error: null,
      loading: true,
    });

    const { golf } = this.props;

    Event.api
      .getMyEvents(apolloClient, { golfId: golf.id })
      .then((events) => {
        const last = [];
        const comingSoon = [];

        events.forEach((b) => {
          const datetime = moment(b.startAt);
          if (datetime.isBefore()) {
            last.push(b);
          } else {
            comingSoon.push(b);
          }
        });

        this.setState({
          last,
          comingSoon,
          loading: false,
          ...(last.length > LIMIT && { showMore: false }),
        });
      })
      .catch((e) => this.setState({ error: e.message }))
      .finally(() => this.setState({ loading: false, loadingMore: false }));
  };

  /* */
  goToView = (event) => () => {
    const { id, eventId } = event;

    if (id) {
      return this.props.history.push(`/myevents/${eventId}/${id}`);
    }

    return this.props.history.push(`/myevents/${eventId}`);
  };

  /* */
  renderItem = (type) => (item, key) => {
    const { classes } = this.props;

    const isCanceled = ['canceled', 'refunded'].includes(item.status);

    const startAt = moment(item.startAt, 'YYYY-MM-DD');
    const registeredAt = item.registeredAt && moment(item.registeredAt, 'YYYY-MM-DD');

    return (
      <Grid item key={key} md={6} sm={10} xs={12}>
        <ButtonBase disableRipple component='div' onClick={this.goToView(item)} className={classes.itemContainer}>
          <Paper
            className={classNames(classes.item, {
              [classes.itemCanceled]: isCanceled,
            })}
          >
            <Box display='flex' flexDirection='row'>
              <CalendarDay datetime={startAt} />
              <Box ml={3} flex={1}>
                <Typography color='secondary' variant='subtitle1'>
                  {Event.functions.sanitize(item.name)}
                </Typography>
                {item.nbEntrants > 0 && (
                  <Chip
                    variant='outlined'
                    icon={<FaceOutlined />}
                    classes={{ icon: classes.chipIcon, outlined: classes.chip }}
                    label={`${i18n.t('terms.entrants', { count: item.nbEntrants })}`}
                  />
                )}
                {type === 'comingSoon' && item.registeredAt && (
                  <Typography variant='caption' className={classes.registeredAt}>
                    {`Inscrit le ${i18n.l('date.formats.default', registeredAt.toDate())}`}
                  </Typography>
                )}
              </Box>
            </Box>
            {isCanceled && (
              <Typography component='span' variant='caption' className={classes.canceled}>
                {'Annulée'}
              </Typography>
            )}
          </Paper>
        </ButtonBase>
      </Grid>
    );
  };

  /* */
  renderEmpty() {
    const { golf } = this.props;
    return (
      <Box textAlign='center'>
        <Typography paragraph variant='h5' align='center' component='h2'>
          {i18n.getFromOpts('event_history.list.not_found', golf.options)}
        </Typography>
        <Button color='secondary' variant='contained' onClick={() => this.props.history.push('/event')}>
          {"S'inscrire"}
        </Button>
      </Box>
    );
  }

  /* */
  renderHistory() {
    const { golf } = this.props;
    const { last, comingSoon, showMore } = this.state;

    if (last.length + comingSoon.length === 0) {
      return this.renderEmpty();
    }

    // reduce array elements
    const renderedLast = showMore === false ? last.filter((l, idx) => idx < LIMIT) : last;

    return (
      <React.Fragment>
        {comingSoon.length > 0 && (
          <Box mb={4}>
            <Typography paragraph variant='h6' component='h2'>
              {i18n.getFromOpts('event_next.sub_title', golf.options)}
            </Typography>
            <Grid container spacing={24}>
              {comingSoon.map(this.renderItem('comingSoon'))}
            </Grid>
          </Box>
        )}
        {last.length > 0 && (
          <Box mb={2}>
            <Typography paragraph variant='h6' component='h2'>
              {i18n.getFromOpts('event_past.sub_title', golf.options)}
            </Typography>
            <Grid container spacing={24}>
              {renderedLast.map(this.renderItem('last'))}
            </Grid>
          </Box>
        )}
        {showMore === false && (
          <Button fullWidth color='secondary' onClick={() => this.setState({ showMore: true })}>
            {'Voir plus'}
          </Button>
        )}
      </React.Fragment>
    );
  }

  /* */
  render() {
    const { golf } = this.props;
    const { error, loading } = this.state;

    return (
      <Screen error={error} loading={loading} title={i18n.getFromOpts('event_past.title', golf.options)}>
        {!loading && this.renderHistory()}
      </Screen>
    );
  }
}

const mapStateToProps = ({ app }) => ({ golf: app.golf });

const StyledComponent = withStyles(styles)(EventHistory);

export default connect(mapStateToProps, Event.actions)(StyledComponent);
